/**
 * LayoutView.jsx — CASCA real do Agendário (Tarefa B). Substitui o ShellScaffold.
 * Pura: recebe { vm, actions, children }. Estiliza só via `sx` + CSS vars (var(--*)).
 *
 * Contrato: shell/layout.vm.js (@version 5)
 *   vm.nav[]      → { key, label, iconName, route, migrated }
 *   vm.header     → { appTitle, appSubtitle, userName, userInitials, notificationsCount,
 *                     whatsapp: { visible, status, creditsLabel, tooltip } }
 *   vm.activeRoute
 *   actions       → onNavigate, onLogout, onOpenNotifications, onOpenSettings,
 *                   onOpenSupportWidget, onOpenWhatsApp
 *   headerExtras  → (slot opcional) nó renderizado no lugar do sino embutido — o app real injeta
 *                   o NotificationCenter completo aqui; sem ele, o sino embutido renderiza (DP).
 * Estado UI-local (permitido): drawer mobile aberto/fechado.
 */
import { useState } from 'react';
import {
  AppBar, Toolbar, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  Box, Typography, Avatar, IconButton, Badge, Stack, Tooltip, CircularProgress,
} from '@mui/material';
import WhatsApp from '@mui/icons-material/WhatsApp';
import MenuRounded from '@mui/icons-material/Menu';
import Settings from '@mui/icons-material/Settings';
import MonitorHeart from '@mui/icons-material/MonitorHeart';
import WorkspacePremium from '@mui/icons-material/WorkspacePremium';
import Dashboard from '@mui/icons-material/Dashboard';
import EventNote from '@mui/icons-material/EventNote';
import CalendarMonth from '@mui/icons-material/CalendarMonth';
import Person from '@mui/icons-material/Person';
import People from '@mui/icons-material/People';
import Business from '@mui/icons-material/Business';
import ContentCut from '@mui/icons-material/ContentCut';
import Payment from '@mui/icons-material/Payment';
import EventBusy from '@mui/icons-material/EventBusy';
import WebAsset from '@mui/icons-material/WebAsset';
import AccountBalanceWallet from '@mui/icons-material/AccountBalanceWallet';
import Assessment from '@mui/icons-material/Assessment';
import Share from '@mui/icons-material/Share';
import Hub from '@mui/icons-material/Hub';
import SupportAgent from '@mui/icons-material/SupportAgent';
import Notifications from '@mui/icons-material/Notifications';
import Logout from '@mui/icons-material/Logout';
import HelpOutline from '@mui/icons-material/HelpOutline';
// Ícones TEMÁTICOS usados pelos temas por segmento (themes/templates/*.json → icons.*.name).
// Tema novo com ícone fora desta lista cai no HelpOutline — registrar aqui (checklist §6b).
import EventAvailable from '@mui/icons-material/EventAvailable';
import Schedule from '@mui/icons-material/Schedule';
import CalendarToday from '@mui/icons-material/CalendarToday';
import Event from '@mui/icons-material/Event';
import HomeWork from '@mui/icons-material/HomeWork';
import Storefront from '@mui/icons-material/Storefront';
import Store from '@mui/icons-material/Store';
import MeetingRoom from '@mui/icons-material/MeetingRoom';
import Weekend from '@mui/icons-material/Weekend';
import LocalHospital from '@mui/icons-material/LocalHospital';
import Face from '@mui/icons-material/Face';
import Group from '@mui/icons-material/Group';
import Groups from '@mui/icons-material/Groups';
import Woman from '@mui/icons-material/Woman';
import PersonSearch from '@mui/icons-material/PersonSearch';
import SelfImprovement from '@mui/icons-material/SelfImprovement';
import BeachAccess from '@mui/icons-material/BeachAccess';
import NaturePeople from '@mui/icons-material/NaturePeople';
import Block from '@mui/icons-material/Block';
import Spa from '@mui/icons-material/Spa';
import MedicalServices from '@mui/icons-material/MedicalServices';
import MiscellaneousServices from '@mui/icons-material/MiscellaneousServices';
import FavoriteBorder from '@mui/icons-material/FavoriteBorder';
import FitnessCenter from '@mui/icons-material/FitnessCenter';
import SportsGymnastics from '@mui/icons-material/SportsGymnastics';
import DirectionsRun from '@mui/icons-material/DirectionsRun';
import Psychology from '@mui/icons-material/Psychology';
import Brush from '@mui/icons-material/Brush';
import ColorLens from '@mui/icons-material/ColorLens';
import School from '@mui/icons-material/School';
import MenuBook from '@mui/icons-material/MenuBook';
import DesignServices from '@mui/icons-material/DesignServices';
import Forum from '@mui/icons-material/Forum';
import BadgeIcon from '@mui/icons-material/Badge';

const ICONS = {
  Dashboard, EventNote, CalendarMonth, Person, People, Business, ContentCut,
  Payment, EventBusy, WebAsset, AccountBalanceWallet, Assessment, Share, Hub, SupportAgent,
  MonitorHeart, WorkspacePremium,
  // temáticos (por segmento)
  EventAvailable, Schedule, CalendarToday, Event, HomeWork, Storefront, Store, MeetingRoom,
  Weekend, LocalHospital, Face, Group, Groups, Woman, PersonSearch, SelfImprovement, BeachAccess,
  NaturePeople, Block, Spa, MedicalServices, MiscellaneousServices, FavoriteBorder, FitnessCenter,
  SportsGymnastics, DirectionsRun, Psychology, Brush, ColorLens, School, MenuBook,
  DesignServices, Forum, Badge: BadgeIcon,
};
const DRAWER_W = 276;

/* ── Paleta do MENU LATERAL ─────────────────────────────────────────────────
 * Todo texto/ícone/linha DENTRO do drawer usa --text-drawer (não a cor global),
 * para que temas de drawer ESCURO com texto global ESCURO continuem legíveis.
 * Fallback: se o tema não definir --text-drawer, cai no texto global — temas
 * de drawer claro ficam idênticos ao comportamento atual.
 * Variações (secundário/linhas) derivam por color-mix — nenhum token novo.
 */
const DRAWER_TEXT = 'var(--text-drawer, var(--text-primary))';
const DRAWER_TEXT_MUTED = `color-mix(in srgb, ${DRAWER_TEXT} 62%, transparent)`;
const DRAWER_LINE = `color-mix(in srgb, ${DRAWER_TEXT} 16%, transparent)`;

/**
 * Logo oficial Agendário.
 * URL interna genérica (Vite: arquivo em /public/brand/agendario-logo.png).
 * Quando a hospedagem definitiva existir (CDN), basta trocar esta constante.
 *
 * NOTA DE RECORTE (arte atual = PNG 640×640, fundo BRANCO, com respiro ao redor do "A"):
 *  - Header usa o arquivo cheio; object-fit: cover recorta o respiro e centraliza — nenhum recorte manual obrigatório.
 *  - Recortes desejáveis quando possível:
 *      1. Versão com fundo TRANSPARENTE (para uso direto sobre superfícies escuras/temas).
 *      2. Favicon: 32×32 e 180×180 (apple-touch), recortada justa no símbolo.
 */
const BRAND_LOGO_URL = '/brand/agendario-logo.png';

export default function LayoutView({ vm, actions, children, headerExtras }) {
  const { nav = [], header = {}, activeRoute } = vm || {};
  const [mobileOpen, setMobileOpen] = useState(false); // UI-local

  const drawerContent = (
    <DrawerContent nav={nav} activeRoute={activeRoute} actions={actions}
      onNavigateDone={() => setMobileOpen(false)} />
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'var(--bg-page)' }}>
      {/* ---------------- Header ---------------- */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          bgcolor: 'var(--surface)',
          color: 'var(--text-primary)',
          borderBottom: '1px solid var(--divider)',
        }}
      >
        <Toolbar sx={{ gap: { xs: .5, md: 1.5 }, minHeight: 64, px: { xs: 1, sm: 2 } }}>
          {/* hamburger (mobile) */}
          <IconButton aria-label="Abrir menu" onClick={() => setMobileOpen(true)}
            sx={{
              display: { xs: 'inline-flex', md: 'none' }, width: 44, height: 44,
              color: 'var(--text-primary)', borderRadius: 'var(--radius-sm)',
              '&:hover': { bgcolor: 'var(--color-primary-soft)' },
              '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '-2px' },
            }}>
            <MenuRounded />
          </IconButton>

          {/* marca — alinhada à largura do drawer no desktop; compacta no mobile */}
          <Stack direction="row" spacing={1.25} alignItems="center"
            sx={{ width: { xs: 'auto', md: DRAWER_W - 24 }, pr: { xs: 1, md: 2 }, minWidth: 0 }}>
            {/* Logo oficial — tile branco garante contraste em qualquer tema (a arte tem fundo branco).
                object-fit: cover preenche o tile e recorta o respiro da arte, centralizado nativamente. */}
            <Box aria-hidden sx={{
              width: 38, height: 38, flexShrink: 0, borderRadius: 'var(--radius-sm)',
              bgcolor: '#fff', border: '1px solid var(--divider)',
              overflow: 'hidden',
              boxShadow: 'var(--shadow-card)',
            }}>
              <Box component="img" src={BRAND_LOGO_URL} alt=""
                sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography noWrap sx={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 17, lineHeight: 1.1, letterSpacing: '.2px' }}>
                {header.appTitle}
              </Typography>
              <Typography noWrap variant="caption" sx={{ color: 'var(--text-secondary)', display: { xs: 'none', sm: 'block' } }}>
                {header.appSubtitle}
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ flex: 1 }} />

          {/* WhatsApp: canal + saldo de créditos (layout.vm @v5) */}
          <HeaderWhatsAppChip wa={header.whatsapp} onOpen={() => actions?.onOpenWhatsApp?.()} />

          {/* cluster de ações: notificações + configurações */}
          <Stack direction="row" spacing={.25} alignItems="center"
            sx={{
              px: .5, py: .25,
              borderRadius: 999, border: '1px solid var(--divider)', bgcolor: 'var(--bg-page)',
            }}>
            {headerExtras || (
              <Tooltip title="Notificações">
                <IconButton aria-label={`Notificações${header.notificationsCount ? `: ${header.notificationsCount} novas` : ''}`}
                  onClick={() => actions?.onOpenNotifications?.()}
                  sx={{
                    width: 40, height: 40, color: 'var(--text-secondary)',
                    '&:hover': { color: 'var(--text-primary)', bgcolor: 'var(--color-primary-soft)' },
                    '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '-2px' },
                  }}>
                  <Badge
                    badgeContent={header.notificationsCount}
                    overlap="circular"
                    sx={{ '& .MuiBadge-badge': { bgcolor: 'var(--color-error)', color: '#fff', fontWeight: 700 } }}
                  >
                    <Notifications sx={{ fontSize: 22 }} />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Configurações da conta">
              <IconButton aria-label="Configurações da conta" onClick={() => actions?.onOpenSettings?.()}
                sx={{
                  width: 40, height: 40, color: 'var(--text-secondary)',
                  '&:hover': { color: 'var(--text-primary)', bgcolor: 'var(--color-primary-soft)' },
                  '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '-2px' },
                }}>
                <Settings sx={{ fontSize: 22 }} />
              </IconButton>
            </Tooltip>
          </Stack>

          <Box sx={{ width: '1px', height: 28, bgcolor: 'var(--divider)', mx: { xs: .5, sm: 1 }, display: { xs: 'none', sm: 'block' } }} />

          {/* usuário + sair */}
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
            <Tooltip title="Configurações da conta">
              <Stack direction="row" spacing={1.25} alignItems="center"
                role="button" tabIndex={0} aria-label="Abrir configurações da conta"
                onClick={() => actions?.onOpenSettings?.()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); actions?.onOpenSettings?.(); }
                }}
                sx={{
                  minWidth: 0, cursor: 'pointer', borderRadius: 'var(--radius-sm)', mx: -.75, px: .75, py: .25,
                  '&:hover': { bgcolor: 'var(--color-primary-soft)' },
                  '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '-2px' },
                }}>
                <Avatar sx={{
                  width: 36, height: 36, flexShrink: 0, fontWeight: 700, fontSize: 15,
                  bgcolor: 'var(--color-primary-soft)', color: 'var(--color-primary)',
                  border: '1px solid var(--color-primary-light)',
                }}>
                  {header.userInitials}
                </Avatar>
                <Box sx={{ display: { xs: 'none', md: 'block' }, lineHeight: 1.15, minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{header.userName}</Typography>
                  <Typography noWrap variant="caption" sx={{ color: 'var(--text-secondary)' }}>Minha conta</Typography>
                </Box>
              </Stack>
            </Tooltip>
            <Tooltip title="Sair">
              <IconButton aria-label="Sair" onClick={() => actions?.onLogout?.()}
                sx={{
                  width: 40, height: 40, color: 'var(--text-secondary)',
                  '&:hover': { color: 'var(--color-error)', bgcolor: 'var(--tint-error-bg)' },
                  '&:focus-visible': { outline: '2px solid var(--color-error)', outlineOffset: '-2px' },
                }}>
                <Logout sx={{ fontSize: 22 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* ---------------- Sidebar (temporário no mobile, permanente no desktop) ---------------- */}
      <Box component="nav" sx={{ width: { md: DRAWER_W }, flexShrink: { md: 0 } }}>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: DRAWER_W, boxSizing: 'border-box',
            bgcolor: 'var(--bg-drawer)', borderRight: '1px solid var(--divider)',
            color: DRAWER_TEXT, display: 'flex', flexDirection: 'column',
          },
        }}
      >
        {drawerContent}
      </Drawer>
      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            width: DRAWER_W, boxSizing: 'border-box',
            bgcolor: 'var(--bg-drawer)', borderRight: '1px solid var(--divider)',
            color: DRAWER_TEXT, display: 'flex', flexDirection: 'column',
          },
        }}
      >
        {drawerContent}
      </Drawer>
      </Box>

      {/* ---------------- Conteúdo ---------------- */}
      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Toolbar />
        <Box sx={{ p: { xs: 2, md: 3 }, flex: 1 }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}

/* ── Chip WhatsApp do header ─────────────────────────────────────────────────
 * Responde de relance: "o canal está funcionando e quanto saldo eu tenho?".
 * Estados (vm.header.whatsapp.status): ok | low | depleted | loading | disabled.
 * Estado NUNCA só por cor: cada um tem texto próprio no chip (desktop) e no
 * aria-label/tooltip (que vem formatado do VM). Clique → onOpenWhatsApp
 * (tela de Integrações). visible:false → não renderiza nada (kill-switch).
 * Desktop = pill com saldo; mobile (xs–sm) = só ícone + badge numérica.
 */
const WA_STYLES = {
  ok: {
    border: 'var(--divider)', bg: 'var(--bg-page)',
    icon: 'var(--wa-action)', fg: 'var(--text-primary)', sub: null,
  },
  low: {
    border: 'var(--tint-warning-border)', bg: 'var(--tint-warning-bg)',
    icon: 'var(--tint-warning-fg)', fg: 'var(--tint-warning-fg)', sub: 'Pouco saldo',
  },
  depleted: {
    border: 'var(--tint-error-border)', bg: 'var(--tint-error-bg)',
    icon: 'var(--tint-error-fg)', fg: 'var(--tint-error-fg)', sub: 'Envios pausados',
  },
  loading: {
    border: 'var(--divider)', bg: 'var(--bg-page)',
    icon: 'var(--text-secondary)', fg: 'var(--text-secondary)', sub: null,
  },
  disabled: {
    border: 'var(--divider)', bg: 'transparent',
    icon: 'var(--slate-400)', fg: 'var(--text-secondary)', sub: 'Ativar',
  },
};

function HeaderWhatsAppChip({ wa, onOpen }) {
  if (!wa || wa.visible === false) return null;
  const s = WA_STYLES[wa.status] || WA_STYLES.ok;
  const focusRing = {
    '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '-2px' },
  };

  return (
    <>
      {/* desktop / tablet largo: pill com saldo */}
      <Tooltip title={wa.tooltip}>
        <Stack direction="row" spacing={.75} alignItems="center"
          role="button" tabIndex={0} aria-label={wa.tooltip}
          onClick={onOpen}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); }
          }}
          sx={{
            display: { xs: 'none', sm: 'flex' },
            height: 40, px: 1.25, cursor: 'pointer', flexShrink: 0,
            borderRadius: 999, border: `1px solid ${s.border}`, bgcolor: s.bg,
            color: s.fg, transition: 'background-color .15s, border-color .15s',
            '&:hover': { bgcolor: 'var(--color-primary-soft)', borderColor: 'var(--color-primary-light)' },
            ...focusRing,
          }}>
          <WhatsApp aria-hidden sx={{ fontSize: 20, color: s.icon }} />
          {wa.status === 'loading' && (
            /* mesmo footprint de um saldo curto → sem layout shift */
            <Box sx={{ width: 28, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={14} thickness={5} sx={{ color: 'var(--text-secondary)' }} />
            </Box>
          )}
          {wa.creditsLabel != null && (
            <Typography component="span" variant="body2"
            sx={{ fontWeight: 700, color: s.fg, lineHeight: 1 }}>
              {wa.creditsLabel}
            </Typography>
          )}
          {s.sub && (
            <Typography component="span" variant="caption" sx={{ color: s.fg, lineHeight: 1, whiteSpace: 'nowrap' }}>
              {s.sub}
            </Typography>
          )}
        </Stack>
      </Tooltip>

      {/* mobile: só ícone + badge com o saldo */}
      <Tooltip title={wa.tooltip}>
        <IconButton aria-label={wa.tooltip} onClick={onOpen}
          sx={{
            display: { xs: 'inline-flex', sm: 'none' },
            width: 44, height: 44, borderRadius: 'var(--radius-sm)', color: s.icon,
            '&:hover': { bgcolor: 'var(--color-primary-soft)' },
            ...focusRing,
          }}>
          <Badge
            overlap="circular"
            badgeContent={wa.creditsLabel ?? undefined}
            invisible={wa.creditsLabel == null}
            sx={{
              '& .MuiBadge-badge': {
                fontWeight: 700,
                bgcolor: wa.status === 'depleted' ? 'var(--color-error)'
                  : wa.status === 'low' ? 'var(--color-warning)' : 'var(--tint-neutral-bg)',
                color: (wa.status === 'depleted' || wa.status === 'low') ? '#fff' : 'var(--tint-neutral-fg)',
                border: wa.status === 'ok' ? '1px solid var(--tint-neutral-border)' : 'none',
              },
            }}>
            <WhatsApp sx={{ fontSize: 22 }} />
          </Badge>
        </IconButton>
      </Tooltip>
    </>
  );
}

/* conteúdo do drawer extraído p/ reuso nos dois variants (mobile/desktop) */
function DrawerContent({ nav, activeRoute, actions, onNavigateDone }) {
  return (
    <>
        <Toolbar />
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Typography variant="caption" sx={{ color: DRAWER_TEXT_MUTED, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase' }}>
            Menu
          </Typography>
        </Box>
        <List sx={{ px: 1, py: 0, flex: 1, overflowY: 'auto' }}>
          {nav.map((item) => {
            const Icon = ICONS[item.iconName] || HelpOutline;
            const selected = item.route === activeRoute;
            const soon = !item.migrated;
            return (
              <ListItemButton
                key={item.key}
                onClick={() => { actions?.onNavigate?.(item.route); onNavigateDone?.(); }}
                aria-current={selected ? 'page' : undefined}
                sx={{
                  position: 'relative', mb: .25, minHeight: 44, borderRadius: 'var(--radius-sm)',
                  color: selected ? 'var(--color-primary)' : DRAWER_TEXT,
                  bgcolor: selected ? 'var(--color-primary-soft)' : 'transparent',
                  opacity: soon && !selected ? 0.62 : 1,
                  transition: 'background-color .15s, color .15s',
                  '&:hover': { bgcolor: 'var(--color-primary-soft)' },
                  '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '-2px' },
                  // barra de acento à esquerda quando ativo
                  '&::before': selected ? {
                    content: '""', position: 'absolute', left: 0, top: 8, bottom: 8, width: 3,
                    borderRadius: 3, bgcolor: 'var(--color-primary)',
                  } : {},
                }}
              >
                <ListItemIcon sx={{ minWidth: 38, color: selected ? 'var(--color-primary)' : DRAWER_TEXT_MUTED }}>
                  <Icon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontSize: 14, fontWeight: selected ? 700 : 500 }}
                />
                {soon && (
                  <Box component="span" sx={{
                    ml: 1, px: .75, py: '2px', borderRadius: 999, flexShrink: 0,
                    border: `1px solid ${DRAWER_LINE}`, color: DRAWER_TEXT_MUTED,
                    fontSize: 10, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase',
                    lineHeight: 1.4, bgcolor: 'transparent',
                  }}>
                    em breve
                  </Box>
                )}
              </ListItemButton>
            );
          })}
        </List>

        <Box sx={{ p: 2, borderTop: `1px solid ${DRAWER_LINE}` }}>
          <Stack direction="row" spacing={1} alignItems="center"
            role="button" tabIndex={0} aria-label="Abrir central de ajuda"
            onClick={() => { actions?.onOpenSupportWidget?.(); onNavigateDone?.(); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); actions?.onOpenSupportWidget?.(); onNavigateDone?.(); }
            }}
            sx={{
              color: DRAWER_TEXT_MUTED, cursor: 'pointer', borderRadius: 'var(--radius-sm)',
              mx: -.75, px: .75, py: .5,
              '&:hover': { bgcolor: 'var(--color-primary-soft)', color: DRAWER_TEXT },
              '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '-2px' },
            }}>
            <HelpOutline sx={{ fontSize: 18 }} />
            <Typography variant="caption">Central de ajuda</Typography>
          </Stack>
        </Box>
    </>
  );
}
